// God-mode toggle in the top nav. When elevated, the badge links to
// the /god dashboard so the operator lands directly on the things
// they're likely to be doing during their session.
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Lock, Unlock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/lib/auth";
import { elevateGod, revokeGod, ApiError } from "@/lib/api";

export function GodToggle() {
  const { me, refresh } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!me?.god.active) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [me?.god.active]);

  if (!me) return null;

  const active = me.god.active;
  const expiresIn = active
    ? Math.max(0, new Date(me.god.expiresAt).getTime() - now)
    : 0;

  async function elevate() {
    setErr(null);
    setBusy(true);
    try {
      await elevateGod();
      await refresh();
      navigate("/god");
    } catch (e) {
      if (e instanceof ApiError) {
        setErr((e.body as { reason?: string })?.reason ?? `${e.status}`);
      } else {
        setErr(e instanceof Error ? e.message : "Failed");
      }
    } finally {
      setBusy(false);
    }
  }

  async function revoke() {
    setBusy(true);
    try {
      await revokeGod();
      await refresh();
    } finally {
      setBusy(false);
    }
  }

  if (active) {
    return (
      <div className="flex items-center gap-2">
        <Link to="/god" aria-label="Open god dashboard">
          <Badge
            variant="accent"
            className="cursor-pointer font-mono text-[10px] hover:opacity-80"
          >
            <Unlock className="mr-1 h-3 w-3" />
            GOD · {formatRemaining(expiresIn)}
          </Badge>
        </Link>
        <Button
          variant="ghost"
          size="sm"
          onClick={revoke}
          disabled={busy}
          aria-label="Revoke god mode"
        >
          Revoke
        </Button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      {err && (
        <span className="text-xs text-destructive" title={err}>
          {err.length > 30 ? err.slice(0, 30) + "…" : err}
        </span>
      )}
      <Button
        variant="ghost"
        size="sm"
        onClick={elevate}
        disabled={busy}
        title="Elevate to god mode"
      >
        <Lock className="mr-1 h-3.5 w-3.5" />
        {busy ? "Elevating…" : "God"}
      </Button>
    </div>
  );
}

function formatRemaining(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}
